import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import { optionsPartido, optionsTipoEntidad } from '../../datos/options';

const SECCIONES = [...new Set(optionsPartido.map(p => p.seccion_electoral).filter(Boolean))].sort();

const TIPOS_ENTIDAD = [
    ...optionsTipoEntidad,
    { value: 'Sin dato', label: 'Sin dato' },
];

const PERSONERIA_OPCIONES = [
    { value: 'Sí', label: 'Tiene' },
    { value: 'No', label: 'No tiene' },
    { value: 'En trámite', label: 'En trámite' },
    { value: 'Sin dato', label: 'Sin dato' },
];

const PREDIO_OPCIONES = [
    { value: 'Sí', label: 'Sí' },
    { value: 'No', label: 'No' },
    { value: 'Sin dato', label: 'Sin dato' },
];

const DEPORTE_ADAPTADO_OPCIONES = [
    { value: 'si', label: 'Sí' },
    { value: 'no', label: 'No' },
    { value: 'Sin dato', label: 'Sin dato' },
];

const SERVICIOS_OPCIONES = [
    { value: 'energia', label: 'Energía' },
    { value: 'agua', label: 'Agua' },
    { value: 'gas', label: 'Gas' },
    { value: 'cloacas', label: 'Cloacas' },
    { value: 'Sin dato', label: 'Sin dato' },
];

const DashboardFilters = ({ filtros, onFilterChange, onReset, loading = false, esMunicipio = false, userMunicipio = null }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const partidoOptions = esMunicipio && userMunicipio
        ? optionsPartido
            .filter(p => p.value === userMunicipio)
            .map(p => ({
                value: p.value,
                label: `${p.label} (${p.seccion_electoral})`,
            }))
        : [
            { value: 'Todos', label: '⚠️ Todos los Partidos' },
            ...optionsPartido
                .filter(p => !filtros.seccion || filtros.seccion.length === 0 || filtros.seccion.includes(p.seccion_electoral))
                .map(p => ({
                    value: p.value,
                    label: `${p.label} (${p.seccion_electoral})`,
                })),
        ];

    const handleChange = (name, value) => {
        if (onFilterChange) {
            onFilterChange({ target: { name, value } });
        }
    };

    const activeFiltersCount = Object.keys(filtros).filter(k => {
        if (Array.isArray(filtros[k])) {
            return filtros[k].length > 0 && !filtros[k].includes('Todos');
        }
        return filtros[k] && filtros[k] !== 'Todas' && filtros[k] !== 'Todos' && filtros[k] !== '';
    }).length;

    const partidosElegidos = (filtros.partido || []).filter(v => v !== 'Todos');
    const seccionesElegidas = useMemo(() => filtros.seccion || [], [filtros.seccion]);
    const seccionesDePartidos = new Set(
        optionsPartido.filter(p => partidosElegidos.includes(p.value)).map(p => p.seccion_electoral)
    );
    const partidosDeDistintasSecciones = partidosElegidos.length > 1 && seccionesDePartidos.size > 1;
    const regionesDisponibles = useMemo(() => {
        let base = optionsPartido;
        if (partidosElegidos.length > 0) {
            base = optionsPartido.filter(p => partidosElegidos.includes(p.value));
        } else if (seccionesElegidas.length > 0) {
            base = optionsPartido.filter(p => seccionesElegidas.includes(p.seccion_electoral));
        }
        return [...new Set(base.map(p => p.region).filter(Boolean))].sort();
    }, [partidosElegidos, seccionesElegidas]);

    useEffect(() => {
        if (filtros.region && regionesDisponibles.length > 0 && !regionesDisponibles.includes(filtros.region)) {
            onFilterChange?.({ target: { name: 'region', value: '' } });
        }
    }, [filtros.region, regionesDisponibles, onFilterChange]);

    const cruceSinResultados = useMemo(() => {
        if (partidosElegidos.length > 0 && seccionesElegidas.length > 0) {
            const compatibles = optionsPartido.filter(p =>
                partidosElegidos.includes(p.value) && seccionesElegidas.includes(p.seccion_electoral)
            );
            if (compatibles.length === 0) return true;
        }
        if (filtros.region && regionesDisponibles.length > 0 && !regionesDisponibles.includes(filtros.region)) {
            return true;
        }
        return false;
    }, [partidosElegidos, seccionesElegidas, filtros.region, regionesDisponibles]);

    const selectClassName = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100';

    return (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm transition-all">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <h3 className="font-semibold text-gray-700">Filtros Activos</h3>
                    <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                        {activeFiltersCount}
                    </span>
                </div>

                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                    {isExpanded ? 'Simplificar' : 'Más filtros'}
                    <svg className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>

            <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {/* Partido (Selección Múltiple) */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
                            Partidos / Municipios (Selección Múltiple)
                        </label>
                        <Select
                            id="partido"
                            isMulti
                            isDisabled={esMunicipio}
                            options={partidoOptions}
                            value={(() => {
                                const current = filtros.partido || [];
                                if (current.includes('Todos') || current.length === 0) {
                                    return [{ value: 'Todos', label: '⚠️ Todos los Partidos' }];
                                }
                                return partidoOptions.filter(o => current.includes(o.value));
                            })()}
                            onChange={(selectedOptions) => {
                                if (!selectedOptions || selectedOptions.length === 0) {
                                    handleChange('partido', ['Todos']);
                                    return;
                                }
                                const valores = selectedOptions.map(o => o.value);
                                let resultado = valores;
                                const teniaTodos = (filtros.partido || []).includes('Todos') || (filtros.partido || []).length === 0;
                                if (valores.includes('Todos') && valores.length > 1) {
                                    resultado = teniaTodos ? valores.filter(v => v !== 'Todos') : ['Todos'];
                                }
                                handleChange('partido', resultado);
                            }}
                            placeholder="Seleccionar partidos..."
                            className="mt-1"
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    background: 'transparent',
                                    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                                    boxShadow: state.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : undefined,
                                    '&:hover': { borderColor: '#3b82f6' },
                                    minHeight: '38px',
                                }),
                                menu: (base) => ({ ...base, zIndex: 50 }),
                            }}
                        />
                        <p className="text-[10px] text-gray-400 mt-1">Seleccioná uno o varios partidos.</p>
                    </div>

                    {/* Región */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Región</label>
                        <select
                            name="region"
                            value={filtros.region || ''}
                            onChange={(e) => handleChange('region', e.target.value)}
                            disabled={loading || partidosDeDistintasSecciones || esMunicipio}
                            className={selectClassName}
                        >
                            <option value="">Todas</option>
                            {regionesDisponibles.map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                        {partidosDeDistintasSecciones && (
                            <p className="text-[10px] text-amber-600 mt-1">Se deshabilita al elegir partidos de distintas secciones.</p>
                        )}
                    </div>

                    {/* Sección electoral */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Sección electoral</label>
                        <Select
                            id="seccion"
                            isMulti
                            isDisabled={esMunicipio}
                            options={SECCIONES.map(s => ({ value: s, label: s }))}
                            value={(filtros.seccion || []).map(s => ({ value: s, label: s }))}
                            onChange={(selectedOptions) => {
                                const valores = (selectedOptions || []).map(o => o.value);
                                handleChange('seccion', valores);
                            }}
                            placeholder="Seleccionar secciones..."
                            className="mt-1"
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    background: 'transparent',
                                    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                                    boxShadow: state.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : undefined,
                                    '&:hover': { borderColor: '#3b82f6' },
                                    minHeight: '38px',
                                }),
                                menu: (base) => ({ ...base, zIndex: 50 }),
                            }}
                        />
                    </div>

                    {/* Tipo de entidad */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Tipo de entidad</label>
                        <select
                            name="tipoEntidad"
                            value={filtros.tipoEntidad || ''}
                            onChange={(e) => handleChange('tipoEntidad', e.target.value)}
                            disabled={loading}
                            className={selectClassName}
                        >
                            <option value="">Todos</option>
                            {TIPOS_ENTIDAD.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {cruceSinResultados && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <svg className="h-4 w-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                            <p className="font-semibold">La combinación de filtros no devuelve resultados</p>
                            <p className="text-amber-700 mt-0.5">Revisá la selección de partidos, región o secciones electorales: la intersección es vacía.</p>
                        </div>
                    </div>
                )}

                {/* Filtros expandidos (Personería / Predio / Deporte adaptado / Servicio) */}
                {isExpanded && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 border-t border-gray-200 pt-4 animate-fadeIn">
                    {/* Personería */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Personería</label>
                        <select
                            name="personeria"
                            value={filtros.personeria || ''}
                            onChange={(e) => handleChange('personeria', e.target.value)}
                            disabled={loading}
                            className={selectClassName}
                        >
                            <option value="">Todas</option>
                            {PERSONERIA_OPCIONES.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Predio propio */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Predio propio</label>
                        <select
                            name="predio"
                            value={filtros.predio || ''}
                            onChange={(e) => handleChange('predio', e.target.value)}
                            disabled={loading}
                            className={selectClassName}
                        >
                            <option value="">Todos</option>
                            {PREDIO_OPCIONES.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Deporte adaptado */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Deporte adaptado</label>
                        <select
                            name="deporteAdaptado"
                            value={filtros.deporteAdaptado || ''}
                            onChange={(e) => handleChange('deporteAdaptado', e.target.value)}
                            disabled={loading}
                            className={selectClassName}
                        >
                            <option value="">Todos</option>
                            {DEPORTE_ADAPTADO_OPCIONES.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Servicio */}
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Servicio</label>
                        <select
                            name="servicio"
                            value={filtros.servicio || ''}
                            onChange={(e) => handleChange('servicio', e.target.value)}
                            disabled={loading}
                            className={selectClassName}
                        >
                            <option value="">Todos</option>
                            {SERVICIOS_OPCIONES.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                    </div>
                )}

                {/* Botón único de Resetear */}
                <div className="mt-4 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                    {loading && (
                        <span className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
                            <svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sincronizando datos...
                        </span>
                    )}
                    <button
                        onClick={onReset}
                        disabled={loading}
                        className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Resetear filtros
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DashboardFilters;
